import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { 
  FaTicketAlt, 
  FaClock, 
  FaCheckCircle, 
  FaExclamationCircle,
  FaSearch,
  FaFilter,
  FaServer
} from 'react-icons/fa';
import './SupportDashboard.css';

const SupportDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    category: '',
    search: ''
  });

  // Redirect if not support team
  useEffect(() => {
    if (user && user.userType !== 'support' && user.userType !== 'admin') {
      toast.error('Access denied. Support team only.');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchSupportData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [tickets, filters]);

  const fetchSupportData = async () => {
    try {
      const [statsRes, ticketsRes] = await Promise.all([
        api.get('/support/stats'),
        api.get('/support/tickets')
      ]);
      
      setStats(statsRes.data.stats);
      setTickets(ticketsRes.data.tickets || []);
    } catch (error) {
      console.error('Failed to fetch support data:', error);
      toast.error('Failed to load support dashboard');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...tickets];

    if (filters.status) {
      filtered = filtered.filter(t => t.status === filters.status);
    }
    if (filters.priority) {
      filtered = filtered.filter(t => t.priority === filters.priority);
    }
    if (filters.category) {
      filtered = filtered.filter(t => t.category === filters.category);
    }
    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(t => 
        t.ticketNumber.toLowerCase().includes(search) ||
        t.subject.toLowerCase().includes(search) ||
        t.user?.businessName?.toLowerCase().includes(search)
      );
    }

    setFilteredTickets(filtered);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      priority: '',
      category: '',
      search: ''
    });
  };

  const getStatusCount = (status) => {
    if (!stats?.statusCounts) return 0;
    const item = stats.statusCounts.find(s => s._id === status);
    return item ? item.count : 0;
  };

  const getTotalTickets = () => {
    return stats?.totalTickets?.[0]?.total || 0;
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'open': return 'badge-warning';
      case 'in_progress': return 'badge-info';
      case 'pending_support': return 'badge-danger';
      case 'pending_user': return 'badge-warning';
      case 'resolved': return 'badge-success';
      case 'closed': return 'badge-secondary';
      default: return 'badge-warning';
    }
  };

  const getPriorityBadgeClass = (priority) => {
    switch (priority) {
      case 'urgent': return 'badge-danger';
      case 'high': return 'badge-warning';
      case 'medium': return 'badge-info';
      case 'low': return 'badge-secondary';
      default: return 'badge-info';
    }
  };

  if (loading) return <Layout><div className="loading">Loading support dashboard...</div></Layout>;

  return (
    <Layout>
      <div className="support-dashboard">
        <div className="dashboard-header">
          <div>
            <h1>Support Team Dashboard</h1>
            <p>Manage and respond to customer support tickets</p>
          </div>
          {user?.userType === 'admin' && (
            <button 
              className="btn btn-outline"
              onClick={() => navigate('/api-logs')}
            >
              <FaServer /> View API Logs
            </button>
          )}
        </div>

        <div className="stats-grid">
          <div className="stat-card card">
            <div className="stat-icon total-icon">
              <FaTicketAlt />
            </div>
            <div className="stat-content">
              <h3>Total Tickets</h3>
              <p className="stat-number">{getTotalTickets()}</p>
            </div>
          </div>

          <div className="stat-card card">
            <div className="stat-icon open-icon">
              <FaExclamationCircle />
            </div>
            <div className="stat-content">
              <h3>Open Tickets</h3>
              <p className="stat-number">{getStatusCount('open')}</p>
            </div>
          </div>

          <div className="stat-card card">
            <div className="stat-icon progress-icon">
              <FaClock />
            </div>
            <div className="stat-content">
              <h3>In Progress</h3>
              <p className="stat-number">{getStatusCount('in_progress')}</p>
            </div>
          </div>

          <div className="stat-card card">
            <div className="stat-icon resolved-icon">
              <FaCheckCircle />
            </div>
            <div className="stat-content">
              <h3>Resolved</h3>
              <p className="stat-number">{getStatusCount('resolved')}</p>
            </div>
          </div>
        </div>

        <div className="filters-section card">
          <div className="filters-header">
            <h3><FaFilter /> Filter Tickets</h3>
            <button className="btn btn-outline-sm" onClick={clearFilters}>
              Clear Filters
            </button>
          </div>
          <div className="filters-grid">
            <div className="filter-group">
              <label>Search</label>
              <div className="search-input">
                <FaSearch />
                <input
                  type="text"
                  placeholder="Ticket # or subject..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </div>
            </div>
            <div className="filter-group">
              <label>Status</label>
              <select 
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Status</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="pending_support">Pending Support</option>
                <option value="pending_user">Pending User</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Priority</label>
              <select 
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
              >
                <option value="">All Priorities</option>
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Category</label>
              <select 
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <option value="">All Categories</option>
                <option value="product">Product</option>
                <option value="order">Order</option>
                <option value="payment">Payment</option>
                <option value="finance">Finance</option>
                <option value="technical">Technical</option>
                <option value="account">Account</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          <div className="filter-results">
            Showing <strong>{filteredTickets.length}</strong> of <strong>{tickets.length}</strong> tickets
          </div>
        </div>

        <div className="tickets-table-section card">
          <h3>All Support Tickets</h3>
          {filteredTickets.length === 0 ? (
            <div className="empty-state">
              <FaTicketAlt style={{ fontSize: '48px', color: '#d1d5db' }} />
              <p>No tickets found matching your filters</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="tickets-table">
                <thead>
                  <tr>
                    <th>Ticket #</th>
                    <th>Subject</th>
                    <th>Customer</th>
                    <th>Category</th>
                    <th>Priority</th>
                    <th>Status</th>
                    <th>Assigned To</th>
                    <th>Created</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTickets.map(ticket => (
                    <tr key={ticket._id}>
                      <td className="ticket-number-cell">
                        {ticket.ticketNumber}
                      </td>
                      <td className="subject-cell">
                        <div className="subject-content">
                          {ticket.subject}
                          {ticket.lastResponseBy === 'user' && (
                            <span className="new-reply-badge">New Reply</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="customer-info">
                          <div className="customer-name">{ticket.user?.businessName || ticket.user?.email}</div>
                          <div className="customer-type">{ticket.userType || ticket.user?.userType}</div>
                        </div>
                      </td>
                      <td>
                        <span className="badge badge-secondary">
                          {ticket.category}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${getPriorityBadgeClass(ticket.priority)}`}>
                          {ticket.priority}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(ticket.status)}`}>
                          {ticket.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td>
                        {ticket.assignedTo ? (
                          <span className="assigned-name">
                            {ticket.assignedTo.businessName || ticket.assignedTo.email}
                          </span>
                        ) : (
                          <span className="unassigned">Unassigned</span>
                        )}
                      </td>
                      <td className="date-cell">
                        {new Date(ticket.createdAt).toLocaleDateString('en-IN')}
                      </td>
                      <td>
                        <button 
                          className="btn btn-primary-sm"
                          onClick={() => navigate(`/support/tickets/${ticket._id}`)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default SupportDashboard;
